// Monthly IRS withholding (retenção na fonte) for employment income, as set out in the yearly Despacho:
//   withholding = R × marginal rate − amount to subtract − (per-dependant amount × dependants), never below zero,
//   rounded down to the euro.
// Holiday and Christmas bonuses are always withheld on their own (art. 99.º-C CIRS), never added to the month's pay.
//
// IRS Jovem at source (art. 99.º-F, n.º 4): work out the rate that would apply to the whole remuneration, then apply
// it only to the part that isn't exempt. The exempt part is capped at (55 × IAS) ÷ 14 per payment, cumulatively.

import { r2 } from '../money.js';

export function findRow(rows, R) {
  return rows.find((r) => r.upTo === null || r.upTo === undefined || R <= r.upTo) || rows[rows.length - 1];
}

// Unrounded withholding for remuneration R
export function rawWithholding(R, table, dependents = 0) {
  if (!(R > 0)) return { amount: 0, rate: 0, deduct: 0, row: null };
  const rowFound = findRow(table.rows, R);
  const rate = rowFound.rate / 100;
  const d = rowFound.deduct;
  const deduct = typeof d === 'number' ? d : rate * d.k * (d.ref - R);
  const amount = Math.max(0, R * rate - deduct - (table.perDependent || 0) * Math.max(0, dependents || 0));
  return { amount, rate: rowFound.rate, deduct: r2(deduct), row: rowFound };
}

// The withholding for one payment, rounded down to the euro: { amount, effRate (%), marginal, deduct, exempt, taxable }
export function withholding(R, params, { table = 'I', dependents = 0, jovemPct = 0, exemptRoom = Infinity } = {}) {
  const tab = params.withholding[table] || params.withholding.I;
  const full = rawWithholding(R, tab, dependents);
  if (!(R > 0)) return { amount: 0, effRate: 0, marginal: 0, deduct: 0, exempt: 0, taxable: 0, full: 0 };
  if (!jovemPct) {
    const amount = Math.floor(full.amount + 1e-9);
    return { amount, effRate: (full.amount / R) * 100, marginal: full.rate, deduct: full.deduct, exempt: 0, taxable: R, full: amount };
  }
  // Effective rate on the whole remuneration, to two decimal places of a percent as the AT examples do
  const effRate = Math.round((full.amount / R) * 10000) / 100;
  const exempt = r2(Math.max(0, Math.min(R * (jovemPct / 100), exemptRoom)));
  const taxable = r2(R - exempt);
  const amount = Math.floor((taxable * effRate) / 100 + 1e-9);
  return { amount, effRate, marginal: full.rate, deduct: full.deduct, exempt, taxable, full: Math.floor(full.amount + 1e-9) };
}
